import { useState, useEffect, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import type { RiskAssessment } from '@/utils/health'
import { detectRegion, type Region } from '@/utils/time'
import { formatStreakCompact } from '@/utils/milestones'
import HealthDropdown from './HealthDropdown'

interface NavBarProps {
  onMenuClick: () => void
  onLogDrink?: () => void
  onLogGap?: () => void
  onDownloadReport?: () => void
  onAwardsClick?: () => void
  onAnalyticsClick?: () => void
  onOpenSettings?: (section?: string) => void
  riskAssessment?: RiskAssessment | null
  soberStreakSeconds?: number
}

const SETTINGS_ITEMS = [
  { id: 'about-you', label: 'About You', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  { id: 'password', label: 'Password', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' },
  { id: 'backup-email', label: 'Backup Email', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  { id: 'backups', label: 'Backups', icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4' },
  { id: 'auto-logout', label: 'Auto Logout', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  { id: 'data', label: 'Data Management', icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z' },
]

export default function NavBar({ onMenuClick, onLogDrink, onLogGap, onDownloadReport, onAwardsClick, onAnalyticsClick, onOpenSettings, riskAssessment, soberStreakSeconds = 0 }: NavBarProps) {
  const { userId } = useParams<{ userId: string }>()
  const [region, setRegion] = useState<Region>(detectRegion)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const settingsRef = useRef<HTMLDivElement>(null)

  // Listen for region changes from settings
  useEffect(() => {
    const handler = (e: Event) => setRegion((e as CustomEvent).detail)
    window.addEventListener('ssdl:region-change', handler)
    return () => window.removeEventListener('ssdl:region-change', handler)
  }, [])

  // Close settings dropdown on click outside
  useEffect(() => {
    if (!settingsOpen) return
    const handleMouseDown = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setSettingsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [settingsOpen])

  return (
    <nav className="sticky top-0 z-40 bg-[var(--bg-secondary)] border-b border-[var(--border)]" role="navigation" aria-label="Main navigation">
      <div className="flex items-center justify-between h-14 px-4">
        {/* Hamburger Menu */}
        <button
          onClick={onMenuClick}
          className="p-2 rounded-lg hover:bg-[var(--bg-card)] transition-colors cursor-pointer"
          aria-label="Open navigation menu"
          data-tooltip="Open navigation menu"
        >
          <svg
            className="w-6 h-6 text-[var(--text-primary)]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        {/* Logo */}
        <Link to={userId ? `/u/${userId}` : '/'} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          {/* Beer glass icon */}
          <svg
            className="w-8 h-8 text-[var(--accent-teal)]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            {/* Glass body */}
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 4h8l-1 16H9L8 4z" />
            {/* Foam top */}
            <ellipse cx="12" cy="4" rx="4" ry="1.5" />
            {/* Bubbles */}
            <circle cx="10" cy="10" r="0.5" fill="currentColor" />
            <circle cx="14" cy="8" r="0.5" fill="currentColor" />
            <circle cx="11" cy="14" r="0.5" fill="currentColor" />
          </svg>
          <div className="hidden sm:block leading-tight">
            <span className="text-[var(--text-primary)] font-bold tracking-wide">
              SSDL<span className="text-[var(--accent-teal)]">.app</span>
            </span>
            <p className="text-[10px] text-[var(--text-secondary)] -mt-0.5">Simple, Secure Drink Log</p>
          </div>
        </Link>

        {/* Right side buttons */}
        <div className="flex items-center gap-1">
          {onLogDrink && (
            <button
              onClick={onLogDrink}
              className="p-2 rounded-lg hover:bg-[var(--bg-card)] transition-colors cursor-pointer"
              aria-label="Log a new drink"
              data-tooltip="Log a new drink"
            >
              <svg className="w-6 h-6 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
          {onLogGap && (
            <button
              onClick={onLogGap}
              className="p-2 rounded-lg hover:bg-[var(--bg-card)] transition-colors cursor-pointer"
              aria-label="Log a tracking gap"
              data-tooltip="Log a tracking gap"
            >
              <svg className="w-6 h-6 text-[var(--accent-orange)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6v12M20 6v12M4 18h16" />
              </svg>
            </button>
          )}
          {onDownloadReport && (
            <HealthDropdown
              riskAssessment={riskAssessment ?? null}
              onDownloadReport={onDownloadReport}
              region={region}
            />
          )}
          {/* Analytics */}
          {onAnalyticsClick && (
            <button
              onClick={onAnalyticsClick}
              className="p-2 rounded-lg hover:bg-[var(--bg-card)] transition-colors cursor-pointer"
              aria-label="View analytics"
              data-tooltip="Analytics"
            >
              <svg className="w-5 h-5 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
              </svg>
            </button>
          )}
          {/* Awards / Sober streak */}
          {onAwardsClick && (
            <button
              onClick={onAwardsClick}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-[var(--bg-card)] transition-colors cursor-pointer"
              aria-label={soberStreakSeconds > 0 ? `Sober streak: ${formatStreakCompact(soberStreakSeconds)}. View awards.` : 'View awards'}
              data-tooltip="Awards & milestones"
            >
              <svg className="w-5 h-5 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0016.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.997 6.997 0 01-5.27 2.772 6.997 6.997 0 01-5.27-2.772" />
              </svg>
              {soberStreakSeconds > 0 && (
                <span className="text-xs font-mono font-bold text-[var(--accent-teal)]">
                  {formatStreakCompact(soberStreakSeconds)}
                </span>
              )}
            </button>
          )}
          {/* Settings dropdown */}
          {onOpenSettings && (
            <div ref={settingsRef} className="relative">
              <button
                onClick={() => setSettingsOpen(prev => !prev)}
                className="p-2 rounded-lg hover:bg-[var(--bg-card)] transition-colors cursor-pointer"
                aria-label="Settings"
                aria-haspopup="menu"
                aria-expanded={settingsOpen}
                data-tooltip="Settings"
              >
                <svg className="w-6 h-6 text-[var(--text-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              {settingsOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-1 bg-[var(--bg-secondary)] border border-[var(--border)] rounded-lg shadow-lg z-50 overflow-hidden whitespace-nowrap min-w-[180px]"
                >
                  {SETTINGS_ITEMS.map(item => (
                    <button
                      key={item.id}
                      role="menuitem"
                      onClick={() => { onOpenSettings(item.id); setSettingsOpen(false) }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-card)] hover:text-[var(--text-primary)] transition-colors cursor-pointer text-left"
                    >
                      <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                      </svg>
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  )
}
